import React, { useState } from 'react';
import { Button, Card, Empty, Select, Space, Spin, Tag, Typography } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { sanitizeTrustedHtml } from '../../../utils/sanitize';
import {
    useAssignMailThreadMutation,
    useGetMailSendersQuery,
    useGetMailThreadQuery,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { IMailMessage } from '../../../services/nestjs-base-api/types';

const { Text, Title } = Typography;

/**
 * Sanitised email HTML, with every link retargeted to a new tab.
 *
 * A message body is someone else's content: following a link in place would
 * navigate the admin app away and lose the reading position, and `rel` keeps the
 * opened page from reaching back through `window.opener`.
 */
const emailBodyHtml = (html: string | null | undefined): string => {
    const clean = sanitizeTrustedHtml(html || '');
    if (!clean) return '';

    const doc = new DOMParser().parseFromString(clean, 'text/html');
    doc.querySelectorAll('a[href]').forEach((anchor) => {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noreferrer noopener');
    });
    // Received mail sizes images for a full-width client; the thread column is
    // narrower, so keep them inside it.
    doc.querySelectorAll('img').forEach((img) => {
        img.setAttribute('style', `${img.getAttribute('style') ?? ''};max-width:100%;height:auto`);
    });

    return doc.body.innerHTML;
};

// Inbound bodies are stored twice: raw, and with the quoted history stripped.
// Show the stripped version and keep the rest behind a toggle, so a long
// back-and-forth does not bury the two lines that are actually new.
const MailMessageCard: React.FC<{ message: IMailMessage }> = ({ message }) => {
    const intl = useIntl();
    const [showQuoted, setShowQuoted] = useState(false);
    const hasQuoted =
        message.direction === 'in' &&
        !!message.strippedHtml &&
        !!message.bodyHtml &&
        message.strippedHtml !== message.bodyHtml;

    const html = hasQuoted && !showQuoted ? message.strippedHtml : message.bodyHtml;

    return (
        <Card
            size="small"
            styles={{ body: { padding: 16 } }}
            style={{
                marginBottom: 12,
                // Outbound sits inset and tinted, the way a sent message reads in
                // any mail client — direction is legible before reading a word.
                marginLeft: message.direction === 'out' ? 32 : 0,
                background: message.direction === 'out' ? '#f6f8ff' : undefined,
            }}
        >
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space wrap size={8}>
                    <Text strong>{message.fromName || message.from}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(message.occurredAt).toLocaleString()}
                    </Text>
                    <Tag color={message.direction === 'out' ? 'blue' : 'default'}>
                        {message.direction === 'out'
                            ? <FormattedMessage id="mail.sent.tag" defaultMessage="Sent" />
                            : <FormattedMessage id="mail.received.tag" defaultMessage="Received" />}
                    </Tag>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <FormattedMessage id="mail.to" defaultMessage="To" />: {message.to.join(', ')}
                    {message.cc.length > 0 && ` · cc: ${message.cc.join(', ')}`}
                </Text>

                <div
                    className="crm-email-body"
                    style={{ marginTop: 8 }}
                    dangerouslySetInnerHTML={{ __html: emailBodyHtml(html) }}
                />

                {hasQuoted && (
                    <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setShowQuoted(!showQuoted)}>
                        {showQuoted
                            ? <FormattedMessage id="mail.hideQuoted" defaultMessage="Hide trimmed content" />
                            : <FormattedMessage id="mail.showQuoted" defaultMessage="Show trimmed content" />}
                    </Button>
                )}

                {message.attachments.length > 0 && (
                    <Space wrap size={8} style={{ marginTop: 8 }}>
                        {message.attachments.map((attachment) => {
                            const chip = (
                                <Tag
                                    icon={<PaperClipOutlined />}
                                    color={attachment.url ? 'blue' : undefined}
                                    style={{ cursor: attachment.url ? 'pointer' : 'default' }}
                                >
                                    {attachment.filename} ({Math.round(attachment.size / 1024)}KB)
                                </Tag>
                            );

                            // A file that arrived before the attachments bucket
                            // was configured has nothing to link to, so it stays
                            // a plain chip rather than a link that 404s.
                            return attachment.url ? (
                                <a
                                    key={attachment.filename}
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={attachment.filename}
                                >
                                    {chip}
                                </a>
                            ) : (
                                <span key={attachment.filename} title={intl.formatMessage({ id: 'mail.attachmentUnavailable' })}>
                                    {chip}
                                </span>
                            );
                        })}
                    </Space>
                )}
            </Space>
        </Card>
    );
};

interface MailThreadViewProps {
    threadKey: string | null;
    onReply: (replyAll: boolean) => void;
}

export const MailThreadView: React.FC<MailThreadViewProps> = ({ threadKey, onReply }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken as string;

    const { data: thread, isFetching } = useGetMailThreadQuery(
        { jwtToken, threadKey: threadKey as string },
        { skip: !threadKey || !jwtToken },
    );
    const { data: senders } = useGetMailSendersQuery({ jwtToken }, { skip: !jwtToken });
    const [assignThread] = useAssignMailThreadMutation();

    if (!threadKey) {
        return (
            <Card style={{ height: '100%' }}>
                <Empty description={intl.formatMessage({ id: 'mail.noThreadSelected' })} />
            </Card>
        );
    }

    if (isFetching && !thread) {
        return <Card style={{ height: '100%', textAlign: 'center' }}><Spin /></Card>;
    }

    const assignedUserId = thread?.messages.find((m) => m.assignedUserId)?.assignedUserId ?? null;

    return (
        <Card
            title={
                <Space direction="vertical" size={2} style={{ width: '100%', minWidth: 0 }}>
                    {/* One line, ellipsised, with the full subject on hover: a
                        long subject was running under the assignee picker. */}
                    <Title
                        level={5}
                        style={{ margin: 0 }}
                        ellipsis={{ tooltip: thread?.subject }}
                    >
                        {thread?.subject}
                    </Title>
                    {thread?.account && (
                        <Space size={6}>
                            {/* The account name links to the card. The second chip
                                says whether they signed up, rather than repeating
                                the name — company and organisation are usually
                                called the same thing. */}
                            <Link to={`/admin/crm?company=${thread.account.companyHash ?? ''}`}>
                                <Tag color="purple">{thread.account.companyName}</Tag>
                            </Link>
                            {thread.account.organisationId ? (
                                <Link to={`/admin/organizations/${thread.account.organisationId}`}>
                                    <Tag color="green">
                                        {thread.account.organisationName &&
                                        thread.account.organisationName !== thread.account.companyName
                                            ? thread.account.organisationName
                                            : <FormattedMessage id="mail.organisation" defaultMessage="Organisation" />}
                                    </Tag>
                                </Link>
                            ) : (
                                <Tag>
                                    <FormattedMessage id="mail.prospect" defaultMessage="Prospect" />
                                </Tag>
                            )}
                        </Space>
                    )}
                </Space>
            }
            extra={
                <Space>
                    <Select
                        size="small"
                        style={{ minWidth: 160 }}
                        allowClear
                        loading={!senders}
                        placeholder={intl.formatMessage({ id: 'mail.unassigned' })}
                        // Hold the value back until the names are in: antd falls
                        // back to rendering the raw id when it cannot label it,
                        // so the picker briefly read "1" instead of "Steve Ford".
                        value={senders?.length ? (assignedUserId ?? undefined) : undefined}
                        options={(senders ?? []).map((sender) => ({ value: sender.userId, label: sender.name }))}
                        onChange={(value) => assignThread({ jwtToken, threadKey, userId: value ?? null })}
                    />
                    <Button onClick={() => onReply(false)}>
                        <FormattedMessage id="mail.reply" defaultMessage="Reply" />
                    </Button>
                    <Button onClick={() => onReply(true)}>
                        <FormattedMessage id="mail.replyAll" defaultMessage="Reply all" />
                    </Button>
                </Space>
            }
            styles={{ body: { maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' } }}
        >
            {thread?.messages.map((message) => (
                <MailMessageCard key={message.id} message={message} />
            ))}
        </Card>
    );
};
